import { useEffect, useState, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  X,
  ChevronRight,
  ShoppingBag,
  ArrowRight,
  Star,
  Heart,
  Shield,
  Package,
  Truck,
  CreditCard,
  Mail,
  CheckCircle,
  TrendingUp as Fire,
  Sparkles
} from 'lucide-react';
import axios from 'axios';
import { useCartWishlist } from '@/context/CartWishlistContext';
import { toast } from 'sonner';
import Navbar from '@/components/Navbar';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// ✅ Hero images served via Cloudinary with f_auto/q_auto/w_1920 so each image
// is auto-converted to WebP/AVIF and capped at a sane width — no manual
// resizing needed, and bandwidth stays low on mobile.
const HERO_IMAGES = [
  {
    src: 'https://res.cloudinary.com/dcn2wn0ja/image/upload/f_auto,q_auto,w_1920/v1782501351/Ky%C5%8Djur%C5%8D_Rengoku_eo3rjk.jpg',
    alt: 'Kyōjurō Rengoku collectible figure'
  },
  {
    src: 'https://res.cloudinary.com/dcn2wn0ja/image/upload/f_auto,q_auto,w_1920/v1782501352/Levi_Ackerman_krqad6.jpg',
    alt: 'Levi Ackerman collectible figure'
  },
  {
    src: 'https://res.cloudinary.com/dcn2wn0ja/image/upload/f_auto,q_auto,w_1920/v1782501352/Deadpool_pm5gri.jpg',
    alt: 'Deadpool collectible figure'
  },
  {
    src: 'https://res.cloudinary.com/dcn2wn0ja/image/upload/f_auto,q_auto,w_1920/v1782501352/SpiderMan_ttlgft.jpg',
    alt: 'Spider-Man collectible figure'
  }
];

// Animated Counter Component
const AnimatedCounter = ({ end, duration = 2000 }) => {
  const [count, setCount] = useState(0);
  const [hasAnimated, setHasAnimated] = useState(false);
  const counterRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !hasAnimated) {
          setHasAnimated(true);
          let startTime = null;
          const animate = (currentTime) => {
            if (!startTime) startTime = currentTime;
            const progress = Math.min((currentTime - startTime) / duration, 1);
            setCount(Math.floor(progress * end));
            if (progress < 1) requestAnimationFrame(animate);
          };
          requestAnimationFrame(animate);
        }
      },
      { threshold: 0.1 }
    );
    if (counterRef.current) observer.observe(counterRef.current);
    return () => { if (counterRef.current) observer.unobserve(counterRef.current); };
  }, [end, duration, hasAnimated]);

  return <span ref={counterRef}>{count}+</span>;
};

// ✅ Hero carousel — manual swipe/drag only (no autoplay), one full-width
// image per slide. Loading is optimized so only the first slide costs
// anything on initial paint:
//   - slide 0: eager + fetchpriority high + <link rel=preload> in <head>
//   - slides 1-3: lazy + fetchpriority low, decoded off the main thread
//
// SCROLL-DRIVEN BEHAVIOR (Apple-style): the wrapper is tall (one viewport
// height per image), and its inner content is `position: sticky`. While the
// user scrolls down through that tall wrapper, the viewport visually "holds"
// in place and we translate the image track horizontally based on how far
// through the wrapper the scroll position is. Once scroll passes the
// wrapper, it unsticks and the rest of the page continues normally.
const HeroCarousel = ({ images }) => {
  const wrapperRef = useRef(null);
  const [progress, setProgress] = useState(0); // 0 -> start, 1 -> last slide fully shown
  // Measured from the real <nav> element instead of hardcoded, so this can
  // never drift out of sync with the actual navbar height.
  const [navbarHeight, setNavbarHeight] = useState(64);
  const lastIndex = images.length - 1;

  useEffect(() => {
    const measureNavbar = () => {
      const nav = document.querySelector('nav');
      const h = nav?.getBoundingClientRect().height;
      if (h) setNavbarHeight(h);
    };
    measureNavbar();
    window.addEventListener('resize', measureNavbar);
    return () => window.removeEventListener('resize', measureNavbar);
  }, []);

  useEffect(() => {
    if (!images.length) return;
    const link = document.createElement('link');
    link.rel = 'preload';
    link.as = 'image';
    link.href = images[0].src;
    link.setAttribute('fetchpriority', 'high');
    document.head.appendChild(link);
    return () => document.head.removeChild(link);
  }, [images]);

  useEffect(() => {
    let frame = null;

    const computeProgress = () => {
      const wrapper = wrapperRef.current;
      if (!wrapper) return;
      const rect = wrapper.getBoundingClientRect();
      const visibleHeight = window.innerHeight - navbarHeight;
      const scrollableDistance = wrapper.offsetHeight - visibleHeight;
      if (scrollableDistance <= 0) return;
      // While stuck, the wrapper's rendered top sits at navbarHeight (not 0)
      // because the sticky child's `top` offset is navbarHeight. So "how far
      // we've scrolled into the pin" is the gap between that offset and the
      // wrapper's current rendered top.
      const scrolledIntoWrapper = navbarHeight - rect.top;
      const clamped = Math.min(1, Math.max(0, scrolledIntoWrapper / scrollableDistance));
      setProgress(clamped);
    };

    const handleScroll = () => {
      if (frame) return;
      frame = requestAnimationFrame(() => {
        frame = null;
        computeProgress();
      });
    };

    computeProgress();
    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleScroll);
      if (frame) cancelAnimationFrame(frame);
    };
  }, [images.length, navbarHeight]);

  const jumpToSlide = (index) => {
    const wrapper = wrapperRef.current;
    if (!wrapper || lastIndex === 0) return;
    const visibleHeight = window.innerHeight - navbarHeight;
    const scrollableDistance = wrapper.offsetHeight - visibleHeight;
    const wrapperTop = wrapper.getBoundingClientRect().top + window.scrollY;
    // The pin engages once page scroll reaches (wrapperTop - navbarHeight).
    const pinStartScrollY = wrapperTop - navbarHeight;
    const targetScroll = pinStartScrollY + (index / lastIndex) * scrollableDistance;
    window.scrollTo({ top: targetScroll, behavior: 'smooth' });
  };

  const slideWidthPercent = 100 / images.length;
  // Track is `images.length * 100%` wide; convert "slides moved" into a
  // percentage of the track's own width for translateX.
  const slidesMoved = progress * lastIndex;
  const trackTranslatePercent = -(slidesMoved * slideWidthPercent);
  const currentIndex = Math.round(slidesMoved);

  return (
    <div
      ref={wrapperRef}
      style={{ height: `${images.length * 100}vh` }}
      className="relative"
      data-testid="hero-scroll-wrapper"
    >
      <div
        className="sticky overflow-hidden"
        style={{ top: `${navbarHeight}px`, height: `calc(100vh - ${navbarHeight}px)` }}
        data-testid="hero-carousel"
      >
        <div
          className="flex h-full"
          style={{
            width: `${images.length * 100}%`,
            transform: `translateX(${trackTranslatePercent}%)`
          }}
        >
          {images.map((img, i) => (
            <div
              key={img.src}
              className="relative h-full flex-shrink-0"
              style={{ width: `${slideWidthPercent}%` }}
            >
              <img
                src={img.src}
                alt={img.alt}
                className="w-full h-full object-cover"
                loading={i === 0 ? 'eager' : 'lazy'}
                fetchpriority={i === 0 ? 'high' : 'low'}
                decoding="async"
                draggable={false}
              />
              <div className="absolute inset-0 bg-gradient-to-r from-black via-black/70 to-transparent" />
            </div>
          ))}
        </div>

        {/* Hero copy overlay — stays fixed in place while images scroll past */}
        <div className="absolute inset-0 z-10 flex items-center pointer-events-none">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
            <div className="max-w-2xl pointer-events-auto">
              <h1 className="text-5xl sm:text-6xl font-bold mb-6 leading-tight" data-testid="hero-title">
                India's Largest <span className="accent-red">Pop Culture</span> Marketplace
              </h1>
              <p className="text-xl text-gray-300 mb-8" data-testid="hero-description">
                Authentic collectibles from Marvel, DC, Anime & more. Join thousands of collectors across India.
              </p>
              <div className="flex flex-wrap gap-4">
                <Link to="/shop" className="btn-primary flex" data-testid="hero-shop-btn">
                  <ShoppingBag className="mr-2" size={20} />
                  Shop Now
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* Dot indicators — click to smooth-scroll to that slide */}
        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-20 flex gap-2">
          {images.map((_, i) => (
            <button
              key={i}
              onClick={() => jumpToSlide(i)}
              className={`h-2 rounded-full transition-all ${
                i === currentIndex ? 'w-8 bg-red-600' : 'w-2 bg-white/40 hover:bg-white/70'
              }`}
              aria-label={`Go to slide ${i + 1}`}
              data-testid={`hero-dot-${i}`}
            />
          ))}
        </div>

        {/* Scroll hint on the first slide only */}
        {currentIndex === 0 && progress < 0.05 && (
          <div className="absolute bottom-6 right-8 z-20 text-sm text-gray-300 animate-bounce hidden sm:block">
            Scroll ↓
          </div>
        )}
      </div>
    </div>
  );
};

// ✅ FIXED: No more per-card API call — uses product.image_url directly from /api/products
const ProductCardWithWishlist = ({ product, isInWishlist, onWishlistToggle }) => {
  return (
    <Link
      to={`/product/${product.id}`}
      className="product-card card-hover group"
      data-testid={`product-${product.id}`}
    >
      <div className="relative">
        <div className="w-full h-64 bg-gray-900 relative">
          {product.image_url ? (
            <img
              src={product.image_url}
              alt={product.name}
              className="w-full h-full object-cover"
              loading="lazy"
              onError={(e) => { e.target.style.display = 'none'; }}
            />
          ) : (
            <div className="w-full h-full flex flex-col items-center justify-center bg-gradient-to-br from-gray-800 to-gray-900">
              <Package size={48} className="text-gray-600 mb-2" />
              <p className="text-gray-500 text-sm text-center px-4">No Image Available</p>
            </div>
          )}
        </div>

        <button
          onClick={(e) => onWishlistToggle(product.id, e)}
          className={`absolute top-4 right-4 p-2 rounded-full backdrop-blur-sm transition-all hover:scale-110 z-10 ${
            isInWishlist
              ? 'bg-red-600 text-white'
              : 'bg-black/50 text-white hover:bg-black/70'
          }`}
          title={isInWishlist ? 'Remove from wishlist' : 'Add to wishlist'}
        >
          <Heart size={20} className={isInWishlist ? 'fill-white' : ''} />
        </button>

        {product.featured && (
          <div className="absolute top-1 left-4 bg-red-600 text-white px-3 py-1 rounded-full text-sm font-semibold flex items-center gap-1">
            <Star size={14} fill="white" /> Featured
          </div>
        )}

        {product.discount_price && (
          <div className="absolute top-9 left-4 bg-red-600 text-white px-2 py-2 rounded text-xs font-semibold">
            -{Math.round((1 - product.discount_price / product.price) * 100)}%
          </div>
        )}

        {product.stock < 5 && product.stock > 0 && (
          <div className="absolute bottom-4 left-4 bg-yellow-600 text-white px-3 py-1 rounded-full text-xs font-semibold">
            Only {product.stock} left!
          </div>
        )}

        {product.stock === 0 && (
          <div className="absolute inset-0 bg-black/70 flex items-center justify-center">
            <span className="text-white font-semibold text-sm">OUT OF STOCK</span>
          </div>
        )}
      </div>

      <div className="p-4">
        <p className="text-sm text-red-500 mb-1">{product.category}</p>
        <h3 className="text-lg font-semibold mb-2 line-clamp-1">{product.name}</h3>

        <div className="flex items-center gap-2 mb-2">
          {product.discount_price ? (
            <>
              <p className="text-2xl font-bold text-red-500">
                ₹{product.discount_price.toLocaleString()}
              </p>
              <p className="text-sm text-gray-500 line-through">
                ₹{product.price.toLocaleString()}
              </p>
            </>
          ) : (
            <p className="text-2xl font-bold accent-red">
              ₹{product.price.toLocaleString()}
            </p>
          )}
        </div>

        {product.rating > 0 && (
          <div className="flex items-center gap-1">
            <Star size={14} className="text-yellow-500 fill-yellow-500" />
            <span className="text-sm text-gray-400">
              {product.rating} ({product.review_count})
            </span>
          </div>
        )}
      </div>
    </Link>
  );
};

const HomeComponent = () => {
  const navigate = useNavigate();
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [newArrivalsProducts, setNewArrivalsProducts] = useState([]);
  const [trendingProducts, setTrendingProducts] = useState([]);
  const [hotSellingProducts, setHotSellingProducts] = useState([]);
  const [preOrderProducts, setPreOrderProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [wishlistItems, setWishlistItems] = useState(new Set());

  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [expandedCategory, setExpandedCategory] = useState(null);
  const [expandedSubcategory, setExpandedSubcategory] = useState(null);
  const [newsletterEmail, setNewsletterEmail] = useState('');

  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);

  // ✅ Cache products in ref so search can use it without re-fetching
  const allProductsCache = useRef([]);

  const searchRef = useRef(null);
  const profileRef = useRef(null);

  const { cartCount, wishlistCount, user, setUser, setCartCount, setWishlistCount, updateWishlistCount } = useCartWishlist();

  const testimonials = [
    {
      id: 1,
      name: "Rahul Sharma",
      avatar: "https://i.pravatar.cc/150?img=12",
      rating: 5,
      text: "Amazing collection of authentic Marvel figures! Delivery was quick and packaging was excellent. Highly recommend CollectrsInc!",
      verified: true,
      location: "Mumbai"
    },
    {
      id: 2,
      name: "Priya Patel",
      avatar: "https://i.pravatar.cc/150?img=45",
      rating: 5,
      text: "Best place to buy anime collectibles in India. Got my limited edition Naruto figure and it's 100% authentic. Love this store!",
      verified: true,
      location: "Bangalore"
    },
    {
      id: 3,
      name: "Arjun Mehta",
      avatar: "https://i.pravatar.cc/150?img=33",
      rating: 5,
      text: "The customer service is outstanding! They helped me track a rare DC collectible and delivered it within 3 days. Absolutely satisfied!",
      verified: true,
      location: "Delhi"
    }
  ];

  // ✅ FIXED: Single products fetch — replaces 4 separate calls
  const fetchAllProducts = async () => {
    try {
      const response = await axios.get(`${API}/products`);
      const all = response.data;

      // Cache for search
      allProductsCache.current = all;

      // Featured
      setFeaturedProducts(all.filter(p => p.featured === true).slice(0, 4));

      // New Arrivals
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
      setNewArrivalsProducts(
        all
          .filter(p => new Date(p.created_at) >= thirtyDaysAgo)
          .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
          .slice(0, 8)
      );

      // Trending — slice() first to avoid mutating original array
      setTrendingProducts(
        all
          .slice()
          .sort((a, b) => (b.review_count || 0) - (a.review_count || 0))
          .slice(0, 8)
      );
    } catch (error) {
      console.error('Failed to fetch products:', error);
    }
  };

  const fetchHotSellingProducts = async () => {
    try {
      const response = await axios.get(`${API}/products/hot-selling?limit=8`);
      if (response.data.success) setHotSellingProducts(response.data.products);
    } catch (error) {
      console.error('Failed to fetch hot selling products:', error);
    }
  };

  const fetchPreOrderProducts = async () => {
    try {
      const response = await axios.get(`${API}/products/pre-orders?limit=8`);
      if (response.data.success) setPreOrderProducts(response.data.products);
    } catch (error) {
      console.error('Failed to fetch pre-order products:', error);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/categories/hierarchy`);
      setCategories(response.data);
    } catch (error) {
      console.error('Failed to fetch categories:', error);
    }
  };

  const fetchWishlist = async () => {
    try {
      const response = await axios.get(`${API}/wishlist`, { withCredentials: true });
      const productIds = new Set(response.data.products.map(p => p.id));
      setWishlistItems(productIds);
    } catch (error) {
      console.error('Failed to fetch wishlist:', error);
    }
  };

  // ✅ FIXED: 4 calls → 1 call on mount
  useEffect(() => {
    fetchAllProducts();
    fetchHotSellingProducts();
    fetchPreOrderProducts();
    fetchCategories();
    if (user) fetchWishlist();
  }, [user]);

  // ✅ FIXED: Search now uses cached products — no API call on keystroke
  useEffect(() => {
    const searchProducts = () => {
      if (searchQuery.trim().length < 2) {
        setSearchResults([]);
        return;
      }
      setSearchLoading(true);
      const q = searchQuery.toLowerCase();
      const results = allProductsCache.current
        .filter(p =>
          p.name.toLowerCase().includes(q) ||
          p.description?.toLowerCase().includes(q) ||
          p.category.toLowerCase().includes(q)
        )
        .slice(0, 5);
      setSearchResults(results);
      setSearchLoading(false);
    };

    const debounce = setTimeout(searchProducts, 300);
    return () => clearTimeout(debounce);
  }, [searchQuery]);

  // Click outside handler
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) setIsSearchOpen(false);
      if (profileRef.current && !profileRef.current.contains(event.target)) setIsProfileModalOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleWishlistToggle = async (productId, e) => {
    e.preventDefault();

    if (!user) {
      toast.error('Please login to manage wishlist');
      window.location.href = `${process.env.REACT_APP_BACKEND_URL}/api/auth/google/login`;
      return;
    }

    const isInWishlist = wishlistItems.has(productId);
    const newWishlistItems = new Set(wishlistItems);
    if (isInWishlist) {
      newWishlistItems.delete(productId);
    } else {
      newWishlistItems.add(productId);
    }
    setWishlistItems(newWishlistItems);

    try {
      if (isInWishlist) {
        await axios.delete(`${API}/wishlist/remove/${productId}`, { withCredentials: true });
        updateWishlistCount(-1);
        toast.success('Removed from wishlist');
      } else {
        await axios.post(`${API}/wishlist/add?product_id=${productId}`, {}, { withCredentials: true });
        updateWishlistCount(1);
        toast.success('Added to wishlist');
      }
    } catch (error) {
      setWishlistItems(wishlistItems);
      toast.error(error.response?.data?.detail || 'Failed to update wishlist');
    }
  };

  const handleNewsletterSubmit = (e) => {
    e.preventDefault();
    if (!newsletterEmail) { toast.error('Please enter your email'); return; }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(newsletterEmail)) { toast.error('Please enter a valid email'); return; }
    toast.success("Thanks for subscribing! We'll be in touch soon. 🎉");
    setNewsletterEmail('');
  };

  const handleLogout = () => {
    setUser(null);
    setCartCount(0);
    setWishlistCount(0);
    setIsProfileModalOpen(false);
    toast.success('Logged out successfully');
    navigate('/');
  };

  const toggleCategory = (categoryTitle) => {
    setExpandedCategory(expandedCategory === categoryTitle ? null : categoryTitle);
    setExpandedSubcategory(null);
  };

  const toggleSubcategory = (subcategoryName) => {
    setExpandedSubcategory(expandedSubcategory === subcategoryName ? null : subcategoryName);
  };

  const handleCategorySelect = (categoryTitle, subcategoryName = null) => {
    setIsSidebarOpen(false);
    let route = '/shop';
    if (categoryTitle) route += `?category=${encodeURIComponent(categoryTitle)}`;
    if (subcategoryName) route += `&type=${encodeURIComponent(subcategoryName)}`;
    navigate(route);
  };

  return (
    <div className="min-h-screen bg-black text-white">
      <Navbar onHamburgerClick={() => setIsSidebarOpen(true)} />

      {/* Sidebar */}
      {isSidebarOpen && (
        <div className="fixed inset-0 z-50" data-testid="sidebar-overlay">
          <div className="absolute inset-0 bg-black/80" onClick={() => setIsSidebarOpen(false)}></div>
          <div className="absolute left-0 top-0 bottom-0 w-80 bg-gray-900 overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold">Menu</h2>
                <button onClick={() => setIsSidebarOpen(false)} className="p-2 hover:bg-white/10 rounded-lg">
                  <X size={24} />
                </button>
              </div>
              <div className="mt-8">
                <h3 className="text-lg font-semibold mb-4 text-gray-400">Categories</h3>
                <div className="space-y-2">
                  {categories.map((category) => (
                    <div key={category.title}>
                      <button
                        onClick={() => toggleCategory(category.title)}
                        className="w-full flex items-center justify-between p-3 hover:bg-white/5 rounded-lg transition"
                      >
                        <span>{category.title}</span>
                        <ChevronRight
                          size={20}
                          className={`transform transition ${expandedCategory === category.title ? 'rotate-90' : ''}`}
                        />
                      </button>
                      {expandedCategory === category.title && (
                        <div className="ml-4 mt-2 space-y-2">
                          {category.subcategories.map((sub) => (
                            <div key={sub.name}>
                              <button
                                onClick={() => toggleSubcategory(sub.name)}
                                className="w-full flex items-center justify-between p-2 hover:bg-white/5 rounded-lg transition text-sm"
                              >
                                <span>{sub.name}</span>
                                <ChevronRight
                                  size={16}
                                  className={`transform transition ${expandedSubcategory === sub.name ? 'rotate-90' : ''}`}
                                />
                              </button>
                              {expandedSubcategory === sub.name && (
                                <div className="ml-4 mt-2 space-y-1">
                                  {sub.characters.map((char) => (
                                    <button
                                      key={char.name}
                                      onClick={() => handleCategorySelect(category.title, sub.name)}
                                      className="w-full text-left p-2 hover:bg-white/5 rounded-lg transition text-sm text-gray-400"
                                    >
                                      {char.name}
                                    </button>
                                  ))}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="pt-16">
        {/* Hero Section — manual swipe/drag carousel, GIF replaced */}
        <HeroCarousel images={HERO_IMAGES} />

        {/* Trust Badges */}
        <section className="py-12 bg-white/5" data-testid="trust-badges-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8">
              {[
                { icon: Shield, title: '100% Authentic', sub: 'Verified Products' },
                { icon: Truck, title: 'Free Shipping', sub: 'On Orders ₹999+' },
                { icon: Package, title: 'Easy Returns', sub: '7 Day Policy' },
                { icon: CreditCard, title: 'Secure Payments', sub: '100% Protected' },
              ].map(({ icon: Icon, title, sub }) => (
                <div key={title} className="flex flex-col items-center text-center p-4">
                  <div className="w-16 h-16 bg-red-600/20 rounded-full flex items-center justify-center mb-4">
                    <Icon className="text-red-500" size={32} />
                  </div>
                  <h3 className="font-bold text-lg mb-1">{title}</h3>
                  <p className="text-sm text-gray-400">{sub}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Categories */}
        {categories.length > 0 && (
          <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-black to-gray-900">
            <div className="max-w-7xl mx-auto">
              <div className="flex justify-between items-center mb-12">
                <div>
                  <h2 className="text-4xl font-bold mb-2">
                    Shop by <span className="accent-red">Category</span>
                  </h2>
                  <p className="text-gray-400">Explore our vast collection of authentic collectibles</p>
                </div>
                <Link to="/shop" className="text-red-500 hover:text-red-400 flex items-center">
                  View All <ArrowRight className="ml-2" size={20} />
                </Link>
              </div>
              <div className="grid md:grid-cols-3 gap-8">
                {categories.slice(0, 3).map((category) => (
                  <Link
                    key={category.id}
                    to={`/shop?category=${category.code}`}
                    className="group relative overflow-hidden rounded-2xl h-80 card-hover"
                  >
                    <img
                      src={category.image_url}
                      alt={category.name}
                      className="w-full h-full object-cover group-hover:scale-110 transition duration-500"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/50 to-transparent flex items-end">
                      <div className="p-6 w-full">
                        <h3 className="text-3xl font-bold text-white mb-2">{category.name}</h3>
                        <p className="text-gray-300">{category.description}</p>
                        <p className="text-red-500 font-bold text-2xl mt-3">
                          <AnimatedCounter end={category.product_count} /> Items
                        </p>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* New Arrivals */}
        {newArrivalsProducts.length > 0 && (
          <section className="py-20 px-4 sm:px-6 lg:px-8">
            <div className="max-w-7xl mx-auto">
              <div className="flex justify-between items-center mb-12">
                <div>
                  <h2 className="text-4xl font-bold mb-2">
                    <Sparkles className="inline text-yellow-500 mr-2" size={32} />
                    New <span className="accent-red">Arrivals</span>
                  </h2>
                  <p className="text-gray-400">Fresh collectibles added in the last 30 days</p>
                </div>
                <Link to="/shop?sort=newest" className="text-red-500 hover:text-red-400 flex items-center">
                  View All <ArrowRight className="ml-2" size={20} />
                </Link>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                {newArrivalsProducts.map(product => (
                  <ProductCardWithWishlist
                    key={product.id}
                    product={product}
                    isInWishlist={wishlistItems.has(product.id)}
                    onWishlistToggle={handleWishlistToggle}
                  />
                ))}
              </div>
            </div>
          </section>
        )}

        {/* Hot Selling */}
        {hotSellingProducts.length > 0 && (
          <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-gray-900 to-black">
            <div className="max-w-7xl mx-auto">
              <div className="flex justify-between items-center mb-12">
                <div>
                  <h2 className="text-4xl font-bold mb-2">🔥 Hot <span className="accent-red">Selling</span></h2>
                  <p className="text-gray-400">Popular picks flying off the shelves</p>
                </div>
                <Link to="/shop?hot_selling=true" className="text-red-500 hover:text-red-400 flex items-center">
                  View All <ArrowRight className="ml-2" size={20} />
                </Link>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                {hotSellingProducts.map(product => (
                  <ProductCardWithWishlist
                    key={product.id}
                    product={product}
                    isInWishlist={wishlistItems.has(product.id)}
                    onWishlistToggle={handleWishlistToggle}
                  />
                ))}
              </div>
            </div>
          </section>
        )}

        {/* Featured */}
        {featuredProducts.length > 0 && (
          <section className="py-20 px-4 sm:px-6 lg:px-8">
            <div className="max-w-7xl mx-auto">
              <div className="flex justify-between items-center mb-12">
                <h2 className="text-4xl font-bold">
                  Featured <span className="accent-red">Collectibles</span>
                </h2>
                <Link to="/shop?featured=true" className="text-red-500 hover:text-red-400 flex items-center">
                  View All <ArrowRight className="ml-2" size={20} />
                </Link>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                {featuredProducts.map(product => (
                  <ProductCardWithWishlist
                    key={product.id}
                    product={product}
                    isInWishlist={wishlistItems.has(product.id)}
                    onWishlistToggle={handleWishlistToggle}
                  />
                ))}
              </div>
            </div>
          </section>
        )}

        {/* Trending */}
        {trendingProducts.length > 0 && (
          <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-black to-gray-900">
            <div className="max-w-7xl mx-auto">
              <div className="flex justify-between items-center mb-12">
                <div>
                  <h2 className="text-4xl font-bold mb-2">
                    <Fire className="inline text-orange-500 mr-2" size={32} />
                    Trending <span className="accent-red">This Week</span>
                  </h2>
                  <p className="text-gray-400">Most popular collectibles among our community</p>
                </div>
                <Link to="/shop?sort=popularity" className="text-red-500 hover:text-red-400 flex items-center">
                  View All <ArrowRight className="ml-2" size={20} />
                </Link>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                {trendingProducts.map(product => (
                  <ProductCardWithWishlist
                    key={product.id}
                    product={product}
                    isInWishlist={wishlistItems.has(product.id)}
                    onWishlistToggle={handleWishlistToggle}
                  />
                ))}
              </div>
            </div>
          </section>
        )}

        {/* Pre-Orders */}
        {preOrderProducts.length > 0 && (
          <section className="py-20 px-4 sm:px-6 lg:px-8">
            <div className="max-w-7xl mx-auto">
              <div className="flex justify-between items-center mb-12">
                <div>
                  <h2 className="text-4xl font-bold mb-2">
                    Pre-<span className="accent-red">Orders</span>
                  </h2>
                  <p className="text-gray-400">Reserve upcoming collectibles before they arrive</p>
                </div>
                <Link to="/shop?pre_order=true" className="text-red-500 hover:text-red-400 flex items-center">
                  View All <ArrowRight className="ml-2" size={20} />
                </Link>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                {preOrderProducts.map(product => (
                  <ProductCardWithWishlist
                    key={product.id}
                    product={product}
                    isInWishlist={wishlistItems.has(product.id)}
                    onWishlistToggle={handleWishlistToggle}
                  />
                ))}
              </div>
            </div>
          </section>
        )}

        {/* Testimonials */}
        <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-gray-900 to-black">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold mb-4">
                What Our <span className="accent-red">Collectors</span> Say
              </h2>
              <p className="text-gray-400">Join thousands of happy collectors across India</p>
            </div>
            <div className="grid md:grid-cols-3 gap-8">
              {testimonials.map((testimonial) => (
                <div key={testimonial.id} className="glass-effect p-6 rounded-2xl">
                  <div className="flex gap-1 mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} size={20} className="text-yellow-500 fill-yellow-500" />
                    ))}
                  </div>
                  <p className="text-gray-300 mb-6 italic">"{testimonial.text}"</p>
                  <div className="flex items-center gap-4">
                    <img src={testimonial.avatar} alt={testimonial.name} className="w-12 h-12 rounded-full" />
                    <div className="flex-1">
                      <p className="font-bold">{testimonial.name}</p>
                      <p className="text-sm text-gray-400">{testimonial.location}</p>
                    </div>
                    {testimonial.verified && (
                      <CheckCircle size={20} className="text-green-500" title="Verified Purchase" />
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Newsletter */}
        <section className="py-20 px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <div className="glass-effect p-8 md:p-12 rounded-3xl text-center">
              <Mail className="mx-auto mb-6 text-red-500" size={48} />
              <h2 className="text-3xl md:text-4xl font-bold mb-4">
                Stay in the <span className="accent-red">Loop</span>
              </h2>
              <p className="text-gray-400 mb-8 text-lg">
                Get exclusive deals, new arrivals, and collector tips delivered to your inbox
              </p>
              <form onSubmit={handleNewsletterSubmit} className="max-w-md mx-auto">
                <div className="flex flex-col sm:flex-row gap-3">
                  <input
                    type="email"
                    placeholder="Enter your email"
                    value={newsletterEmail}
                    onChange={(e) => setNewsletterEmail(e.target.value)}
                    className="flex-1 px-6 py-4 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:border-red-500"
                  />
                  <button type="submit" className="btn-primary px-8 py-4 whitespace-nowrap">
                    Subscribe
                  </button>
                </div>
                <p className="text-xs text-gray-500 mt-3">
                  By subscribing, you agree to our Privacy Policy and consent to receive updates
                </p>
              </form>
            </div>
          </div>
        </section>
      </div>

      {/* Footer */}
      <footer className="bg-black border-t border-white/10 mt-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div className="space-y-4">
              <img
                src="https://customer-assets.emergentagent.com/job_popcult-bazaar/artifacts/aozrkof5_Collect-I-con%20logo%20black-01.jpg"
                alt="CollectrsInc"
                className="h-16 w-16 rounded-full"
              />
              <p className="text-sm text-gray-400">
                India's premier destination for pop culture collectibles. Marvel, DC, Anime & more.
              </p>
            </div>
            <div>
              <h4 className="font-semibold text-lg mb-4 accent-red">Quick Links</h4>
              <ul className="space-y-2 text-sm text-gray-400">
                <li><Link to="/shop" className="hover:text-red-500 transition">Shop</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-lg mb-4 accent-red">Categories</h4>
              <ul className="space-y-2 text-sm text-gray-400">
                {categories.slice(0, 5).map((category) => (
                  <li key={category.id}>
                    <Link to={`/shop?category=${category.code}`} className="hover:text-red-500 transition">
                      {category.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-lg mb-4 accent-red">Connect With Us</h4>
              <p className="text-sm text-gray-400">Join our community of passionate collectors!</p>
            </div>
          </div>
          <div className="border-t border-white/10 mt-8 pt-8 text-center text-sm text-gray-400">
            <p>&copy; 2025 CollectrsInc. All rights reserved. Built with passion for collectors across India.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default HomeComponent;